import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import restaurantUsersService, { type RestaurantUser, type CreateRestaurantUser, type UpdateRestaurantUser } from '@/services/restaurantUsersService';

export interface UserFormData {
  id?: number;
  email: string;
  full_name: string;
  password: string;
  role: 'admin' | 'staff' | 'customer';
  staff_type: 'waiter' | 'cashier' | 'kitchen' | 'general' | null;
  is_active: boolean;
}

export interface SubmissionResult {
  success: boolean;
  subscriptionLimitError?: string;
  generalError?: string;
  validationErrors?: Array<{ field: string; message: string }>;
}

export function useUserFormSubmission() {
  const { t } = useI18n();
  const saving = ref(false);
  const errorMessage = ref('');
  const subscriptionLimitError = ref('');

  async function submitUserForm(
    formData: UserFormData,
    mode: 'create' | 'edit',
    existingUser?: RestaurantUser | null
  ): Promise<SubmissionResult> {
    saving.value = true;
    errorMessage.value = '';
    subscriptionLimitError.value = '';

    try {
      if (mode === 'create') {
        await restaurantUsersService.createUser(formData as CreateRestaurantUser);
      } else if (existingUser) {
        const updateData: UpdateRestaurantUser = {
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          staff_type: formData.staff_type,
          is_active: formData.is_active
        };
        
        // Only include password if it was changed
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        await restaurantUsersService.updateUser(existingUser.id, updateData);
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error saving user:', error);
      
      // Handle subscription limit errors (403)
      if (error.response?.status === 403) {
        const message = error.response?.data?.detail || 
                       error.response?.data?.error?.message || 
                       'Límite de suscripción alcanzado. Por favor mejora tu plan.';
        subscriptionLimitError.value = message;
        return { 
          success: false, 
          subscriptionLimitError: message 
        };
      }
      
      // Handle backend validation errors
      if (error.response?.data?.error?.validation_errors) {
        const validationErrors = error.response.data.error.validation_errors;
        const formattedErrors = validationErrors.map((err: any) => ({
          field: err.field.replace('body.', ''), // Remove 'body.' prefix
          message: err.message
        }));
        
        // Show general message if we have validation errors
        errorMessage.value = 'Por favor corrige los errores de validación';
        
        return { 
          success: false, 
          generalError: errorMessage.value,
          validationErrors: formattedErrors
        };
      } else if (error.response?.data?.error?.message) {
        // Handle other error types
        errorMessage.value = error.response.data.error.message;
        return { 
          success: false, 
          generalError: errorMessage.value 
        };
      } else if (error.response?.data?.detail) {
        // Handle HTTPException detail format
        errorMessage.value = error.response.data.detail;
        return { 
          success: false, 
          generalError: errorMessage.value 
        };
      } else {
        errorMessage.value = t('users.errors.save_failed');
        return { 
          success: false, 
          generalError: errorMessage.value 
        };
      }
    } finally {
      saving.value = false;
    }
  }

  function resetSubmissionState() {
    saving.value = false;
    errorMessage.value = '';
    subscriptionLimitError.value = '';
  }

  return {
    // State
    saving,
    errorMessage,
    subscriptionLimitError,
    
    // Methods
    submitUserForm,
    resetSubmissionState
  };
}
